/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useMemo } from 'react';
import ToolbarDropdown from './ToolbarDropdown';
import { useGetDealRowQuery } from '@/features/deals/dealsApi';
import {
	ADDITIONAL_GOALS_OF_COVER,
	COMMERCIAL,
	GOALS_OF_COVER_LABELS,
	NEEDS_ANALYSIS_LABELS,
} from '@/components/forms/CreateNewBusinessForm/validationSchema';
import { formatDateString } from '@/utils/constants';
import {
	CLAIM,
	DEAL,
	FIELDS,
	INSIGHT_CLAIM,
	normaliseEmailHtml,
	removeWhitespaceFromHtml,
} from '@/utils/emailHelper';
import { useGetInsightClaimQuery } from '@/features/claims/ticketsApi';
import testDealJson from '@/assets/json/test-deal.json';
import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useGetClassesOfRiskQuery } from '@/features/classesOfRisk/classesOfRiskApi';
import { useLazyGetEmailTemplateBlockByTypeQuery } from '@/features/templates/templatesApi';
import Handlebars from '@/utils/handlebars';

const DealDropdown = ({ dealId, onChange }) => {
	const { username: email } = useSelector((state) => state.msalAccount.account);

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);
	const countryCode = userDetails?.broker?.country ?? 'NZ';
	const brokerGroupIds = userDetails?.brokerGroupArray;

	const { data: dealRow, ...dealRowQuery } = useGetDealRowQuery(dealId, { skip: !dealId });
	const { data: insightClaim, ...insightClaimQuery } = useGetInsightClaimQuery(dealId, {
		skip: !dealRow?.isClaim && dealId !== testDealJson.id,
	});

	const { data: classesOfRisk, ...classesOfRiskQuery } = useGetClassesOfRiskQuery(
		{ countryCode, clientType: dealRow?.clientType ?? COMMERCIAL, brokerGroupIds },
		{ skip: userDetails.isLoading || userDetails.isUninitialized },
	);

	const [getBlockType] = useLazyGetEmailTemplateBlockByTypeQuery();

	const getNeedsAnalysisTable = async (field) => {
		const needsAnalysis = dealRow.needsAnalysis.map((r) => ({
			key: classesOfRisk.find((i) => i.classOfRisk === r.key)?.label,
			value: NEEDS_ANALYSIS_LABELS[r.value],
		}));

		const block = await getBlockType({ type: `${field.type}.${field.value}` }).unwrap();
		const template = Handlebars.compile(
			block.html.toString().replace(/>\s+</g, '><').replace(/\n/g, ''),
		);

		const html = removeWhitespaceFromHtml(
			template({
				needsAnalysis,
			}),
		);

		onChange(normaliseEmailHtml(html));
	};

	const fields = useMemo(() => {
		return (
			FIELDS.filter((field) =>
				!field.block && dealId === testDealJson.id
					? [DEAL, CLAIM, INSIGHT_CLAIM].includes(field.type)
					: dealRow?.isClaim
						? [CLAIM, INSIGHT_CLAIM].includes(field.type)
						: field.type === DEAL,
			)
				// .filter((field) => Boolean(dealRow?.[field.value]))
				.map((field) => {
					const value = ([DEAL, CLAIM].includes(field.type) ? dealRow : insightClaim)?.[
						field.reliesOn ?? field.value
					];
					const disabled =
						value == null ||
						(typeof value === 'object'
							? Array.isArray(value)
								? value.length === 0
								: Object.keys(value).length === 0
							: String(value ?? '').trim().length === 0);
					return {
						...field,
						disabled: dealId === testDealJson.id ? false : disabled,
					};
				})
		);
	}, [dealRow, insightClaim]);

	const getNodeValue = async (event) => {
		const field = fields.find((v) => v.value === event.target.value);
		const object = [DEAL, CLAIM].includes(field.type) ? dealRow : insightClaim;

		if (field.value === 'needsAnalysisTable') {
			return await getNeedsAnalysisTable(field);
		}

		console.log('OBJECT', object);
		const templateAttr = `template-value="${field.type}.${field.value}"`;
		let dealValue =
			field.value === 'goalsOfCover'
				? [
						...(object?.[field.value] ?? []).map((v) => GOALS_OF_COVER_LABELS[v] ?? ''),
						object?.[ADDITIONAL_GOALS_OF_COVER] ?? '',
					].filter((v) => v.length > 0)
				: object?.[field.value];

		console.log('Deal value', dealValue, 'field value', field.value);

		if (field.value === 'riskClasses') {
			console.log('deal value', dealValue);
			dealValue = dealValue
				.map(
					(classOfRisk) =>
						classesOfRisk.find((i) => i.classOfRisk === classOfRisk)?.label,
				)
				.filter(Boolean)
				.join(', ');
		}
		const value =
			typeof dealValue === 'string' || typeof dealValue === 'number'
				? field.label.toLowerCase().includes('date')
					? formatDateString(dealValue)
					: dealValue
				: dealValue && typeof dealValue === 'object' && Array.isArray(dealValue)
					? dealValue.join(', ')
					: dealValue && typeof dealValue === 'object'
						? Object.entries(dealValue)
								.map(([k, v]) => `${k}: ${v}`)
								.join(', ')
						: '';

		let node = `<${field.node} ${templateAttr}>${value}</${field.node}>`;
		if (field.node === 'a') {
			node = `<a ${templateAttr} href="${
				field.label.toLowerCase().includes('email') ? `mailto:${value}` : value
			}">${field.label.toLowerCase().includes('email') ? value : field.label}</a>`;
		} else if (field.node === 'img') {
			node = `<img ${templateAttr} src="${value}" alt="${field.label}" />`;
		} else if (field.node === 'ul' || field.node === 'ol') {
			if (dealValue && typeof dealValue === 'object' && Array.isArray(dealValue)) {
				node = `<${field.node} ${templateAttr}>`;
				if (field.value === 'needsAnalysis') {
					const rows = dealValue
						.filter(
							(val) =>
								val.key &&
								classesOfRisk.find((i) => i.classOfRisk === val.key)?.label &&
								val.value &&
								NEEDS_ANALYSIS_LABELS[val.value],
						)
						.map(
							(val) =>
								`<li ${templateAttr}>${
									classesOfRisk.find((i) => i.classOfRisk === val.key)?.label
								}: ${NEEDS_ANALYSIS_LABELS[val.value]}</li>`,
						);
					node += rows.join('');
				} else {
					node += dealValue.map((val) => Object.entries(val).join(', '));
				}
				node += `</${field.node}>`;
				console.log('NODE', node);
			}
		}
		onChange(node);
	};

	return (
		<>
			{dealId && (
				<ToolbarDropdown
					title={dealRow?.isClaim ? 'claim' : 'deal'}
					fields={fields}
					onChange={getNodeValue}
				/>
			)}
		</>
	);
};

export default DealDropdown;
